import type { CharacterAppearance } from '@seven-dawns/shared';

/**
 * Local "account-less" sessions. Joining a match yields a secret player token which we keep in
 * localStorage so the player can come back to the match from the same device.
 *
 * Stores are namespaced so demo mode never touches real sessions.
 */

export interface MatchSession {
  code: string;
  matchName: string;
  playerId: string;
  playerToken: string;
  playerName: string;
  joinedAtMs: number;
}

export interface PendingJoin {
  code: string;
  matchName: string;
  requestId: string;
  requestToken: string;
  playerName: string;
  requestedAtMs: number;
  /** Team asked for, so the pending screen can name it. */
  teamId?: 0 | 1;
  character?: CharacterAppearance;
  /** The team had only AI captains when asked: they approve automatically. */
  aiApproves?: boolean;
}

/** The last name and character the player used, to prefill the next join. */
export interface SavedProfile {
  name: string;
  character: CharacterAppearance;
}

function read<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function write(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage may be full or blocked (private mode). Sessions then last for this tab only.
  }
}

const normalise = (code: string) => code.trim().toUpperCase();

export function createSessionStore(prefix = 'seven-dawns') {
  const SESSIONS_KEY = `${prefix}:sessions:v1`;
  const PENDING_KEY = `${prefix}:pending:v1`;
  return {
    all(): MatchSession[] {
      return read<MatchSession[]>(SESSIONS_KEY, []).sort((a, b) => b.joinedAtMs - a.joinedAtMs);
    },

    get(code: string): MatchSession | undefined {
      return this.all().find((s) => s.code === normalise(code));
    },

    save(session: MatchSession): void {
      const others = this.all().filter((s) => s.code !== normalise(session.code));
      write(SESSIONS_KEY, [{ ...session, code: normalise(session.code) }, ...others]);
    },

    remove(code: string): void {
      write(
        SESSIONS_KEY,
        this.all().filter((s) => s.code !== normalise(code)),
      );
    },

    allPending(): PendingJoin[] {
      return read<PendingJoin[]>(PENDING_KEY, []);
    },

    pending(code: string): PendingJoin | undefined {
      return read<PendingJoin[]>(PENDING_KEY, []).find((p) => p.code === normalise(code));
    },

    savePending(pending: PendingJoin): void {
      const others = read<PendingJoin[]>(PENDING_KEY, []).filter((p) => p.code !== normalise(pending.code));
      write(PENDING_KEY, [{ ...pending, code: normalise(pending.code) }, ...others]);
    },

    clearPending(code: string): void {
      write(
        PENDING_KEY,
        read<PendingJoin[]>(PENDING_KEY, []).filter((p) => p.code !== normalise(code)),
      );
    },
  };
}

export type SessionStore = ReturnType<typeof createSessionStore>;

export function createProfileStore(prefix = 'seven-dawns') {
  const PROFILE_KEY = `${prefix}:profile:v1`;
  return {
    load(): SavedProfile | undefined {
      return read<SavedProfile | undefined>(PROFILE_KEY, undefined);
    },
    save(p: SavedProfile): void {
      write(PROFILE_KEY, p);
    },
  };
}

export type ProfileStore = ReturnType<typeof createProfileStore>;

/** Small per-match key/value memory on this device (last seen time, onboarding, prompts). */
export function createMatchMemory(prefix = 'seven-dawns') {
  const key = (code: string, name: string) => `${prefix}:m:${normalise(code)}:${name}`;
  return {
    get<T>(code: string, name: string, fallback: T): T {
      return read<T>(key(code, name), fallback);
    },
    set(code: string, name: string, value: unknown): void {
      write(key(code, name), value);
    },
  };
}

export type MatchMemory = ReturnType<typeof createMatchMemory>;

/** Live (real server) stores. */
export const sessions = createSessionStore();
export const profile = createProfileStore();
export const matchMemory = createMatchMemory();
